import React, { useState, useEffect } from 'react';
import api from './api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ExportModal = ({ token, onClose, filters, language, states = [] }) => {
  const [availableColumns, setAvailableColumns] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportLimit, setExportLimit] = useState(10000);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Filter states for export
  const [exportState, setExportState] = useState(filters.state || '');
  const [exportDistrict, setExportDistrict] = useState(filters.district || '');
  const [exportBoard, setExportBoard] = useState(filters.board || '');
  const [exportHasContact, setExportHasContact] = useState('');
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    loadAvailableColumns();
  }, []);
  
  useEffect(() => {
    if (exportState) {
      loadDistricts(exportState);
    } else {
      setDistricts([]);
      setExportDistrict('');
    }
  }, [exportState]);
  
  const loadDistricts = async (state) => {
    const res = await api.get(`/api/school-finder/districts?state=${state}`, token);
    if (res.success) {
      setDistricts(res.districts || []);
    }
  };

  const loadAvailableColumns = async () => {
    setLoading(true);
    const res = await api.get('/api/school-finder/available-columns', token);
    if (res.success) {
      setAvailableColumns(res);
      const allCols = Object.keys(res.all_columns);
      setSelectedColumns(allCols);
    }
    setLoading(false);
  };

  const toggleCategory = (categoryColumns, checked) => {
    if (checked) {
      setSelectedColumns(prev => [...new Set([...prev, ...categoryColumns])]);
    } else {
      setSelectedColumns(prev => prev.filter(c => !categoryColumns.includes(c)));
    }
  };

  const toggleColumn = (col) => {
    if (selectedColumns.includes(col)) {
      setSelectedColumns(prev => prev.filter(c => c !== col));
    } else {
      setSelectedColumns(prev => [...prev, col]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(Object.keys(availableColumns?.all_columns || {}));
    }
    setSelectAll(!selectAll);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/school-finder/export-with-columns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          columns: selectAll ? [] : selectedColumns,
          state: exportState || null,
          district: exportDistrict || null,
          board: exportBoard || null,
          source: filters.source || null,
          has_contact: exportHasContact || null,
          has_whatsapp: filters.has_whatsapp || null,
          search: filters.search || null,
          limit: exportLimit,
          format: exportFormat
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `schools_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        onClose();
      } else {
        alert('Export failed. Please try again.');
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Export error: ' + err.message);
    }
    setExporting(false);
  };

  const isCategoryFullySelected = (categoryColumns) => {
    return categoryColumns.every(c => selectedColumns.includes(c));
  };

  const isCategoryPartiallySelected = (categoryColumns) => {
    const selected = categoryColumns.filter(c => selectedColumns.includes(c));
    return selected.length > 0 && selected.length < categoryColumns.length;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="export-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            📥 {language === 'hi' ? 'Export Schools - Column Selection' : 'Export Schools - Column Selection'}
          </h2>
          <p className="text-green-100 text-sm mt-1">
            {language === 'hi' ? 'जो columns चाहिए वो select करें' : 'Select columns you want to export'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              {/* Filter Options */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-800 mb-3">🔍 {language === 'hi' ? 'Filter Data (Export से पहले)' : 'Filter Data (Before Export)'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                    <select 
                      value={exportState} 
                      onChange={(e) => setExportState(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    >
                      <option value="">All States</option>
                      {states.map(s => (
                        <option key={s.state} value={s.state}>{s.state} ({s.count?.toLocaleString()})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
                    <select 
                      value={exportDistrict} 
                      onChange={(e) => setExportDistrict(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                      disabled={!exportState}
                    >
                      <option value="">All Districts</option>
                      {districts.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Board</label>
                    <select 
                      value={exportBoard} 
                      onChange={(e) => setExportBoard(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    >
                      <option value="">All Boards</option>
                      <option value="CBSE">CBSE</option>
                      <option value="ICSE">ICSE</option>
                      <option value="State">State Board</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                    <select 
                      value={exportHasContact} 
                      onChange={(e) => setExportHasContact(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg text-sm"
                    >
                      <option value="">All</option>
                      <option value="yes">With Contact Only</option>
                      <option value="no">Without Contact</option>
                    </select>
                  </div>
                </div>
                {(exportState || exportDistrict || exportBoard || exportHasContact) && (
                  <div className="mt-2 text-xs text-blue-600">
                    📋 Filters Applied: {exportState && `State: ${exportState}`} {exportDistrict && `| District: ${exportDistrict}`} {exportBoard && `| Board: ${exportBoard}`} {exportHasContact && `| Contact: ${exportHasContact}`}
                  </div>
                )}
              </div>

              {/* Export Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'hi' ? 'Format' : 'Format'}
                  </label>
                  <select 
                    value={exportFormat} 
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="xlsx">Excel (.xlsx)</option>
                    <option value="csv">CSV (.csv)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'hi' ? 'Max Records' : 'Max Records'}
                  </label>
                  <select 
                    value={exportLimit} 
                    onChange={(e) => setExportLimit(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="1000">1,000</option>
                    <option value="5000">5,000</option>
                    <option value="10000">10,000</option>
                    <option value="25000">25,000</option>
                    <option value="50000">50,000</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSelectAll}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectAll 
                        ? 'bg-green-100 text-green-700 border-2 border-green-500' 
                        : 'bg-gray-100 text-gray-700 border border-gray-300'
                    }`}
                  >
                    {selectAll ? '✅ All Selected' : '☐ Select All'}
                  </button>
                </div>
              </div>

              {/* Selected Count */}
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {language === 'hi' ? 'Selected Columns:' : 'Selected Columns:'} 
                  <span className="font-bold text-green-600 ml-1">{selectedColumns.length}</span>
                  <span className="text-gray-400"> / {Object.keys(availableColumns?.all_columns || {}).length}</span>
                </span>
                <span className="text-xs text-gray-500">
                  {filters.state && `State: ${filters.state}`}
                  {filters.district && ` | District: ${filters.district}`}
                </span>
              </div>

              {/* Column Categories */}
              <div className="space-y-4">
                {availableColumns?.categories && Object.entries(availableColumns.categories).map(([catKey, cat]) => (
                  <div key={catKey} className="border rounded-xl overflow-hidden">
                    <div 
                      className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleCategory(cat.columns, !isCategoryFullySelected(cat.columns))}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isCategoryFullySelected(cat.columns)}
                          ref={el => {
                            if (el) el.indeterminate = isCategoryPartiallySelected(cat.columns);
                          }}
                          onChange={() => {}}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                        <span className="font-semibold text-gray-800">{language === 'hi' ? cat.label_hi : cat.label}</span>
                        <span className="text-xs text-gray-500">({cat.columns.length} columns)</span>
                      </div>
                      <span className="text-xs text-green-600 font-medium">
                        {cat.columns.filter(c => selectedColumns.includes(c)).length}/{cat.columns.length} selected
                      </span>
                    </div>
                    <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 bg-white">
                      {cat.columns.map(col => (
                        <label 
                          key={col} 
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm transition-colors ${
                            selectedColumns.includes(col) ? 'bg-green-50 text-green-800' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(col)}
                            onChange={() => toggleColumn(col)}
                            className="w-3 h-3 text-green-600 rounded"
                          />
                          <span className="truncate" title={availableColumns.all_columns[col]?.label}>
                            {availableColumns.all_columns[col]?.label || col}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            {language === 'hi' ? 'बंद करें' : 'Cancel'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || selectedColumns.length === 0}
            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
              exporting || selectedColumns.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {language === 'hi' ? 'Exporting...' : 'Exporting...'}
              </>
            ) : (
              <>
                📥 {language === 'hi' ? `Export ${selectedColumns.length} Columns` : `Export ${selectedColumns.length} Columns`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
